<script setup lang="ts">
import { computed, watch, type PropType } from 'vue';
import GaiaFormField from '../base/GaiaFormField.vue';
import GaiaInput from '../base/inputs/GaiaInput.vue';
import GaiaSelect from '../base/selects/GaiaSelect.vue';
import GaiaSectionHeader from '../base/GaiaSectionHeader.vue';
import GaiaTextarea from '../base/inputs/GaiaTextarea.vue';
import { useSigningProviderOptions } from '@/composables/useSigningProviderOptions';
import { isUnavailableExpiryDay } from '@/composables/useSigningWizard';
import type { SigningProvider } from '@/api';
import type { SignatureInitializeForm } from '@/types/contractManagement/signatureFlow';
import GaiaDatePicker from '../base/inputs/GaiaDatePicker.vue';
import GaiaLink from '../base/GaiaLink.vue';
import GaiaTooltip from '../base/GaiaTooltip.vue';
import { IconInfo } from '@/components/design/icons';
import { useAuthStore } from '@/stores/auth';

/**
 * The request details, under the documents in the wizard's first step: what it is called, who runs
 * it, when it expires and what the signers are told. The provider list is the
 * organisation's `available_signing_providers` (useSigningProviderOptions), so
 * it honours both the integration and the Gaia signing feature.
 */

const props = defineProps({
  form: {
    type: Object as PropType<SignatureInitializeForm>,
    required: true,
  },
  /** Validation message for the name's content, owned by the wizard's gates. */
  nameContentError: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** Validation message for the expiration date, owned by the wizard's gates. */
  expirationError: {
    type: String as PropType<string | null>,
    default: null,
  },
  /** The provider can't change once the request exists — it isn't in the update payload. */
  providerLocked: {
    type: Boolean,
    default: false,
  },
  /** True once the step has been submitted at least once, so misses may be shown. */
  touched: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ 'update:form': [form: SignatureInitializeForm] }>();

const { providerOptions, defaultProvider } = useSigningProviderOptions();

const authStore = useAuthStore();
/**
 * No organisation default yet: say where one is set (admins) or who sets it.
 * With a single provider there is nothing to default between, so no hint.
 */
const showDefaultHint = computed(
  () => !props.providerLocked && !defaultProvider.value && providerOptions.value.length > 1
);

// A new request starts on the organisation's default provider (else the first
// it can use — what the backend would pick). Only an empty choice is filled: a
// restored draft keeps its pick, and an existing request's is locked.
watch(
  [defaultProvider, providerOptions],
  () => {
    if (props.form.provider || props.providerLocked) return;
    const provider =
      defaultProvider.value ?? (providerOptions.value[0]?.value as SigningProvider | undefined);
    if (provider) updateForm({ provider });
  },
  { immediate: true }
);

const nameError = computed(() =>
  props.touched && props.form.name.trim().length === 0
    ? 'A name is required.'
    : (props.nameContentError ?? undefined)
);
const providerError = computed(() =>
  props.touched && !props.form.provider ? 'Pick a provider.' : undefined
);

/** n-date-picker works in epoch millis; the form keeps a datetime-local string. */
const expirationTimestamp = computed(() => {
  if (!props.form.expiration_date) return null;

  const parsed = new Date(props.form.expiration_date);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
});

function updateForm(patch: Partial<SignatureInitializeForm>): void {
  emit('update:form', { ...props.form, ...patch });
}

function updateExpiration(timestamp: number | null): void {
  updateForm({
    expiration_date: timestamp === null ? '' : toDatetimeLocal(endOfLocalDay(timestamp)),
  });
}

/** The form holds an expiry day at 23:59 local, so today still counts as in the future. */
function endOfLocalDay(timestamp: number): Date {
  const day = new Date(timestamp);
  day.setHours(23, 59, 0, 0);
  return day;
}

function toDatetimeLocal(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  const hours = `${value.getHours()}`.padStart(2, '0');
  const minutes = `${value.getMinutes()}`.padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}
</script>

<template>
  <!-- Flat inside the documents step's panel: a label, then the fields — no
       box of its own, which inside the panel's card was a box in a box. -->
  <n-flex vertical :size="14" :wrap="false">
    <gaia-section-header>Request</gaia-section-header>
    <gaia-form-field
      label="Name"
      required
      hint="The subject of the signing email."
      :error="nameError"
    >
      <gaia-input
        data-tour="signing.init-name"
        :value="props.form.name"
        placeholder="Named after the document you pick"
        @update:value="value => updateForm({ name: value })"
      />
    </gaia-form-field>

    <gaia-form-field label="Provider" required :error="providerError">
      <template v-if="showDefaultHint" #action>
        <gaia-tooltip placement="top-end">
          <template #trigger>
            <n-icon
              :component="IconInfo"
              :size="15"
              aria-label="About the default provider"
              :style="{ display: 'block', cursor: 'help', opacity: 0.6 }"
            />
          </template>
          Your organisation has no default signing provider, so the first available one is picked.
          <template v-if="authStore.userPermissions.is_org_admin">
            You can set a default in
            <gaia-link :to="{ name: 'settings-organisation' }">Organisation settings</gaia-link>.
          </template>
          <template v-else>An organisation admin can set one.</template>
        </gaia-tooltip>
      </template>
      <gaia-select
        data-tour="signing.init-provider"
        :value="props.form.provider ?? null"
        :options="providerOptions"
        :disabled="props.providerLocked"
        placeholder="Please select"
        @update:value="value => updateForm({ provider: value })"
      />
    </gaia-form-field>

    <gaia-form-field label="Expires" :error="props.expirationError ?? undefined">
      <!-- A day, not a time: the provider keeps only the date. -->
      <gaia-date-picker
        type="date"
        clearable
        :is-date-disabled="isUnavailableExpiryDay"
        :style="{ width: '100%' }"
        :value="expirationTimestamp"
        :status="props.expirationError ? 'error' : undefined"
        @update:value="updateExpiration"
      />
    </gaia-form-field>

    <gaia-form-field label="Message" hint="Optional · shown in the signing email">
      <gaia-textarea
        :value="props.form.message"
        :autosize="{ minRows: 2, maxRows: 8 }"
        placeholder="Please countersign before month end."
        @update:value="value => updateForm({ message: value })"
      />
    </gaia-form-field>
  </n-flex>
</template>
